import { COMPARE_LABEL, delta, memberLabel, shortDay } from './format';
import {
  type Block,
  type Envelope,
  type LensDocument,
  type LensDocumentData,
  type PageEntry,
  isColumn,
  isSection,
  isTabs,
} from './types';

export const ALSO_SHOW = 'Also show …';

const TOP = 5;

function blocksOf(page: Array<PageEntry>): Array<Block> {
  return page.flatMap((entry) =>
    isSection(entry)
      ? entry.rows.flatMap((row) => row.flatMap((item) => (isColumn(item) ? item : [item])))
      : isTabs(entry)
        ? entry.tabs.flatMap((tab) => blocksOf(tab.page))
        : entry.flatMap((item) => (isColumn(item) ? item : [item])),
  );
}

function splitLabel(block: Block, data: LensDocumentData | undefined): string | undefined {
  const planned = data?.blocks[block.id];
  const envelope = planned?.query ? data?.queries[planned.query] : undefined;
  return envelope?.columns.find((column) => column.role === 'dim')?.label?.toLowerCase();
}

const plural = (label: string) =>
  label.endsWith('s') ? label : label.endsWith('y') ? `${label.slice(0, -1)}ies` : `${label}s`;

function envelopeOf(block: Block | undefined, data: LensDocumentData | undefined): Envelope | undefined {
  const planned = block ? data?.blocks[block.id] : undefined;
  return planned?.query ? data?.queries[planned.query] : undefined;
}

function moved(row: Record<string, unknown> | undefined, before: Record<string, unknown> | undefined, column: Envelope['columns'][number], against: string): string | undefined {
  const change = delta(row?.[column.name] as number | null, before?.[column.name] as number | null, column);
  if (!change || change.arrow === '■' || change.relative === undefined || Math.abs(change.relative) < 0.01) return undefined;
  return `${change.arrow === '▲' ? 'up' : 'down'} ${change.text} ${COMPARE_LABEL[against] ?? ''}`.trim();
}

type Column = Envelope['columns'][number];
type Word = (column: Column) => string;

function wordFor(document: LensDocument, data: LensDocumentData | undefined): Word {
  return (column) => {
    const dataset = document.datasets.find((entry) => entry.id === column.dataset);
    const sql = dataset && 'sql' in dataset ? dataset.sql : '';
    const switched = Object.entries(document.params).find(([, param]) => 'sql' in param && param.role === 'measure' && new RegExp(`@${param.sql}\\b`).test(sql));
    const current = switched ? data?.params[switched[0]] : undefined;
    return current !== undefined && current !== null ? String(current) : column.label;
  };
}

function headlineQuestion(figures: Envelope | undefined, word: Word): string | undefined {
  const column = figures?.columns.find((entry) => entry.role === 'measure');
  const against = Object.keys(figures?.compare ?? {})[0];
  if (!figures || !column || !against) return undefined;
  const change = moved(figures.rows[0], figures.compare[against]?.[0], column, against);
  return change ? `Why is ${word(column)} ${change}?` : undefined;
}

function moverQuestion(ranked: Envelope | undefined): string | undefined {
  const dim = ranked?.columns.find((entry) => entry.role === 'dim');
  const column = ranked?.columns.find((entry) => entry.role === 'measure');
  const against = Object.keys(ranked?.compare ?? {})[0];
  if (!ranked || !dim || !column || !against) return undefined;
  const before = new Map(ranked.compare[against].map((row) => [String(row[dim.name]), row]));
  const candidates = ranked.rows.filter((row) => !row.other && !row.blank).slice(0, 10).map((row) => {
    const base = before.get(String(row[dim.name]));
    const change = delta(row[column.name] as number | null, base?.[column.name] as number | null, column);
    return { row, base, relative: change?.relative ?? 0 };
  }).filter((entry) => entry.base && Math.abs(entry.relative) >= 0.05);
  const biggest = candidates.sort((a, b) => Math.abs(b.relative) - Math.abs(a.relative))[0];
  const change = biggest ? moved(biggest.row, biggest.base, column, against) : undefined;
  return biggest && change ? `Why is ${memberLabel(String(biggest.row[dim.name]))} ${change}?` : undefined;
}

function peakQuestion(trend: Envelope | undefined, word: Word): string | undefined {
  const column = trend?.columns.find((entry) => entry.role === 'measure');
  const grain = trend?.columns.find((entry) => entry.role === 'time')?.label;
  if (!trend || !column || grain !== 'day' || trend.rows.length < 7) return undefined;
  const peak = trend.rows.reduce<Record<string, unknown> | undefined>((best, row) => (typeof row[column.name] === 'number' && (!best || (row[column.name] as number) > (best[column.name] as number)) ? row : best), undefined);
  return typeof peak?.t === 'string' ? `What drove ${word(column)} on ${shortDay(peak.t)}?` : undefined;
}

export function lensStarters(document: LensDocument, data: LensDocumentData | undefined): Array<string> {
  const blocks = blocksOf(document.page);
  const family = (block: Block) => data?.blocks[block.id]?.family;
  const trend = blocks.find((block) => family(block) === 'time');
  const ranked = blocks.find((block) => family(block) === 'breakdown');
  const dimension = ranked ? splitLabel(ranked, data) : undefined;
  const word = wordFor(document, data);
  const asked = [
    headlineQuestion(envelopeOf(blocks.find((block) => family(block) === 'figures'), data), word),
    moverQuestion(envelopeOf(ranked, data)),
    peakQuestion(envelopeOf(trend, data), word),
  ].filter((question): question is string => Boolean(question));
  if (asked.length) return [ALSO_SHOW, ...asked].slice(0, 4);
  const starters = [ALSO_SHOW];
  if (trend && dimension && !trend.by?.length) starters.push(`Split the trend by ${dimension}`);
  if (ranked && dimension) starters.push(`Only the top ${TOP} ${plural(dimension)}`);
  if ((document.period.delta ?? 'pct') !== 'diff') starters.push('Show the changes in dollars');
  starters.push('What moved most over the period in view?');
  return starters.slice(0, 4);
}

export function blockStarters(block: Block, data: LensDocumentData | undefined, document: LensDocument): Array<string> {
  const family = data?.blocks[block.id]?.family;
  const dimension = splitLabel(block, data);
  const otherSplit = blocksOf(document.page)
    .filter((entry) => entry.id !== block.id && data?.blocks[entry.id]?.family === 'breakdown')
    .map((entry) => splitLabel(entry, data))
    .find(Boolean);
  if (family === 'time')
    return [
      otherSplit && !block.by?.length ? `Split this by ${otherSplit}` : 'Show this weekly',
      document.period.compare.includes('ly') ? 'Show the change in dollars here' : 'Compare this with last year',
      'What drove the highest point here?',
    ];
  if (family === 'breakdown')
    return [
      `Only the top ${TOP}${dimension ? ` ${plural(dimension)}` : ''} here`,
      'Show this as a table',
      'What drives the change here?',
    ];
  if (family === 'table')
    return ['Show this as ranked bars', `Only the top ${TOP} rows`, 'What stands out in this table?'];
  if (family === 'figures')
    return ['Show these changes in dollars', 'Hide the compared values here', 'What moved these figures most?'];
  return ['Summarise this in two sentences', 'What does this mean for the period in view?'];
}
